import { motion, AnimatePresence } from "framer-motion";
import { Check, Clock, Lock, Sparkles } from "lucide-react";
import {
  CHORD_PATH_MISSIONS,
  isChordPathMissionUnlocked,
  type ChordPathMission,
} from "../../../lib/chords/chordPathPlan";
import type { ChordPathProgress } from "../../../lib/chords/chordPathProgress";
import { BEGINNER_PATH_THEME as PATH } from "../../../lib/beginnerPath/beginnerPathTheme";
import { BEGINNER_PATH_EXERCISE_GRID_CLASS } from "../../../lib/beginnerPath/beginnerPathLayout";

export interface ChordPathLibraryProps {
  progress: ChordPathProgress;
  pathMode?: boolean;
  preview?: boolean;
  onSelectMission: (missionId: string) => void;
}

export function ChordPathLibrary({
  progress,
  pathMode = false,
  preview = false,
  onSelectMission,
}: ChordPathLibraryProps) {
  const completed = progress.completedMissionIds.length;
  const total = CHORD_PATH_MISSIONS.length;

  return (
    <div className={`w-full ${pathMode ? "pb-8" : "py-4"}`}>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          {!pathMode && (
            <>
              <p className="text-xs tracking-widest uppercase text-[#99f6e4]/70">
                Camino 2 · Familia Em / E / Am
              </p>
              <h2 className="mt-2 font-serif text-2xl font-light text-white/90">
                Primeros acordes
              </h2>
            </>
          )}
          <p className={`text-sm ${pathMode ? "" : "mt-2"} text-white/55`}>
            {completed}/{total} misiones · geometría + sonido limpio
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/45">
          <Sparkles className="h-4 w-4 text-[#99f6e4]" />
          Mástil + mic
        </div>
      </div>

      <div className={BEGINNER_PATH_EXERCISE_GRID_CLASS}>
        <AnimatePresence mode="popLayout">
          {CHORD_PATH_MISSIONS.map((mission) => (
            <MissionCard
              key={mission.id}
              mission={mission}
              completed={progress.completedMissionIds.includes(mission.id)}
              unlocked={isChordPathMissionUnlocked(
                mission,
                progress.completedMissionIds,
                preview
              )}
              onOpen={() => onSelectMission(mission.id)}
            />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

function MissionCard({
  mission,
  completed,
  unlocked,
  onOpen,
}: {
  mission: ChordPathMission;
  completed: boolean;
  unlocked: boolean;
  onOpen: () => void;
}) {
  return (
    <motion.button
      type="button"
      layout
      disabled={!unlocked}
      onClick={onOpen}
      className="relative text-left rounded-2xl border p-5 transition-all disabled:opacity-45 disabled:cursor-not-allowed"
      style={{
        borderColor: completed ? PATH.cardBorderLavender : PATH.cardBorder,
        background: completed ? "rgba(196, 181, 253, 0.08)" : PATH.cardBg,
      }}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <span
          className="w-9 h-9 rounded-xl flex items-center justify-center text-sm font-bold"
          style={{ background: "rgba(196, 181, 253, 0.15)", color: "#c4b5fd" }}
        >
          {mission.order}
        </span>
        {completed ? (
          <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-400/90">
            <Check className="w-3.5 h-3.5" /> Hecho
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[10px] text-white/40">
            <Clock className="w-3 h-3" /> ~{mission.timeEstimateMin} min
          </span>
        )}
      </div>
      <h3 className="font-serif text-lg text-white/90">{mission.title}</h3>
      <p className="mt-1 text-xs text-white/50">{mission.subtitle}</p>
      <p className="mt-2 text-[11px] text-white/40 line-clamp-2">{mission.description}</p>
      <p className="mt-3 text-[10px] uppercase tracking-[0.12em] text-[#99f6e4]/60">
        {mission.shapeId} ·{" "}
        {mission.evalMode === "arpeggio-mono" ? "Arpegio mono" : "Rasgueo"}
      </p>
      {unlocked && (
        <span className="mt-4 inline-flex items-center gap-1.5 text-[11px] font-medium text-[#c4b5fd]/80">
          Abrir misión →
        </span>
      )}
      {!unlocked && (
        <span className="mt-4 inline-flex items-center gap-1 text-[11px] text-white/35">
          <Lock className="w-3 h-3" /> Bloqueada
        </span>
      )}
    </motion.button>
  );
}
